import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import PageContainer from '../../../components/PageContainer';
import Loader from '../../../components/Loader';
import Error from '../../../components/Error';
import { BookingWizardContext, EMPTY_BOOKING, migrateStoredBooking } from './BookingWizardContext';

// Contenitore del wizard di prenotazione (/send-request/:activityId/*).
// - Carica i dati dell'attività (una volta per tutto il wizard).
// - Mantiene lo stato della prenotazione e lo persiste in sessionStorage: il back di sistema
//   (Android incluso) naviga tra gli step senza perdita dati, e un refresh ripristina il lavoro.
// - Espone tutto via BookingWizardContext agli step figli (Outlet).

const STORAGE_PREFIX = 'ticino-wild-booking-';
const STEPS = ['opzione', 'data', 'partecipanti', 'riepilogo'];

const BookingWizardLayout = () => {
    const { activityId } = useParams();
    const { i18n } = useTranslation();
    const token = localStorage.getItem('ticino-wild-token');

    // Fetch dati attività (con lang: il server merge-a le traduzioni EN/FR/DE su titolo,
    // sommario, note opzioni e "Cosa è incluso" — stesso pattern di ActivityDetails)
    const [activityData, setActivityData] = useState(null);
    const [fetchStatus, setFetchStatus] = useState('loading');
    useEffect(() => {
        if (!activityId) return;
        const getActivity = async () => {
            try {
                const url = `${process.env.REACT_APP_SERVER_BASE_URL}/activities/get-activity-user/${activityId}?lang=${i18n.language}`;
                const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
                const response = await fetch(url, { method: 'GET', headers });
                if (response.ok) {
                    const result = await response.json();
                    setActivityData(result.payload);
                    setFetchStatus('succeeded');
                } else {
                    const errorData = await response.json();
                    throw new Error(errorData?.message || 'Errore sconosciuto dal server');
                }
            } catch (error) {
                console.error('Errore durante il fetch:', error);
                setFetchStatus('failed');
            }
        };
        getActivity();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activityId, i18n.language]);

    // Stato prenotazione, idratato da sessionStorage (chiave per attività)
    const storageKey = `${STORAGE_PREFIX}${activityId}`;
    const [booking, setBooking] = useState(() => {
        try {
            const stored = sessionStorage.getItem(storageKey);
            // migrateStoredBooking: converte l'eventuale stato pre multi-opzione ({ bundleId, quantity })
            if (stored) return { ...EMPTY_BOOKING, ...migrateStoredBooking(JSON.parse(stored)) };
        } catch (error) {
            console.error('Ripristino prenotazione non riuscito:', error);
        }
        return EMPTY_BOOKING;
    });
    useEffect(() => {
        try {
            sessionStorage.setItem(storageKey, JSON.stringify(booking));
        } catch (error) {
            console.error('Salvataggio prenotazione non riuscito:', error);
        }
    }, [booking, storageKey]);

    // Patch parziale dello stato (comodo negli step)
    const updateBooking = (patch) => setBooking(prevState => ({ ...prevState, ...patch }));

    // Ripartire da zero (dopo l'invio o su richiesta)
    const resetBooking = (patch = {}) => setBooking({ ...EMPTY_BOOKING, ...patch });

    // Indicatore di avanzamento (pallini) in base allo step corrente
    const location = useLocation();
    const currentStepIndex = useMemo(() => {
        const segment = location.pathname.split('/').pop();
        return STEPS.indexOf(segment);
    }, [location.pathname]);

    const contextValue = { activityId, activityData, booking, updateBooking, resetBooking };

    return (
        <PageContainer items="center">
            {fetchStatus === 'loading' && <Loader />}
            {fetchStatus === 'failed' && <Error message="Impossibile caricare l'esperienza. Riprova più tardi." />}
            {
                fetchStatus === 'succeeded' && activityData &&
                <div className="w-full md:max-w-xl flex flex-col items-center gap-6">
                    {
                        currentStepIndex >= 0 &&
                        <div className="flex gap-2 mt-2">
                            {STEPS.map((step, index) => (
                                <div key={step} className={`h-2 rounded-full transition-all ${index === currentStepIndex ? 'w-6 bg-[#225424]' : 'w-2 bg-neutral-300'}`}></div>
                            ))}
                        </div>
                    }
                    <BookingWizardContext.Provider value={contextValue}>
                        <Outlet />
                    </BookingWizardContext.Provider>
                </div>
            }
        </PageContainer>
    );
};

export default BookingWizardLayout;
